<template>
	<tr  class="single-document-link-component has-elements-visible-on-hover">
		<td class="text-nowrap pr-2 position-relative">
			<span class="d-flex">
				<span class="flex-grow-1">
					<template v-if="item.fileExists">
						<span>
							<span class="item-link font-weight-semibold text-primary clickable"
							@mouseenter= "mouseEnterHandler" @mouseleave= "mouseLeaveHandler" @click="itemTitleClickedHandler" :title="getTitle"
							>{{ getDocumentName }}</span>
							<tippy-component v-if="showTooltip" class="position-absolute"
								:animate-fill="false"
								arrow
								@onCreate="onCreateTippyHandler"
								animation="fade"
								distant="7"
								hideOnClick=false
								interactive
								maxWidth="none"
								placement="left-end"
								theme="light">
								<iframe :src="item.previewUrl" class= "document-preview-tippy-tooltip-iframe" @mouseleave= "showTooltip = false" :style="{ '--height': clientHeight, '--width': clientWidth }"></iframe>
							</tippy-component>
						</span>
						<span class="status-icons-container">
							<i v-if="item.pendrive" class="fab fa-usb ml-2" :class="seriesIncludedInPendrive ? 'text-secondary' : 'text-warning'" :title="seriesIncludedInPendrive ? $t('item_included_in_pendrive') : $t('item_included_in_pendrive_not_series')"></i>
							<i v-if="item.comment" class="far fa-comment-dots ml-2 text-secondary" :title="item.comment.text"></i>
							<i v-if="item.isFavorite" class="ml-2"  :class="getFavoriteClass" :title="item.isFavoriteInThisSeries ? $t('document_link_favorite_in_this_series') : $t('document_link_favorite_in_other_series')"></i>
						</span>
					</template>
					<template v-else>
						<span>{{ getDocumentName }}</span>
						<span class="status-icons-container">
							<i class="fas fa-exclamation-triangle ml-2 text-danger" :title="$t('document_not_found')"></i>
						</span>
					</template>
				</span>
				<span v-if="contextualMenuIcons.length" class="flex-grow-0 contextual-menu-container has-elements-visible-on-hover pl-2">
					<i class="fas fa-ellipsis-v clickable clickable--no-hover text-primary text-right px-2 visible-on-hover" @click="contextMenuIconClickedHandler"></i>
					<ModalContextMenu class="contextual-menu"
						:icons="contextualMenuIcons"
						@escKeyPressed="escKeyPressedHandler"/>
				</span>
			</span>
		</td>
		<td class="text-nowrap">
			<span class="d-flex">
				<span class="flex-grow-1">{{ item.category && item.category.title }}<i class="fas fa-eye-slash text-primary visible-on-hover text-primary clickable clickable--no-hover ml-3" @click="clickCategoryFilterHandler" :title="$t('hideCategory')"></i></span>
			</span>
		</td>
		<td class="text-nowrap">
			<span class="d-flex">
				<span class="flex-grow-1">{{ item.subcategory && item.subcategory.title }}<i class="fas fa-eye-slash text-primary visible-on-hover text-primary clickable clickable--no-hover ml-3" @click="clickSubcategoryFilterHandler" :title="$t('hideSubcategory')"></i></span>
			</span>
		</td>
		<td class="white-space-pre-line">{{ item.languagesScreen }}</td>
		<td class="white-space-pre-line">{{ item.description || '' }}</td>
		<td class="text-nowrap pr-3" :class="hasUpdates ? 'updated-ribbon' : ''">{{ (item.fileExists && item.documentModified.format('YYYY.MM.DD hh:mm')) || '' }}</td>
	</tr>
</template>

<script>
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import ModalContextMenu from '../modals/ContextMenu.vue';
import { utilities } from '../resources/utilities.js';

export default {
	components: {
		ModalContextMenu,
	},

	props: ['clientHeight', 'clientWidth', 'item', 'displayFamilyUpdates', 'seriesIncludedInPendrive', 'userCanEditItems'],

	data() {
		return {
			sortedBy: null,
			showTooltip: false,
			showContextMenu: false,
			isFirstTime: false,
			showTooltipTimer: null,
		};
	},

	computed: {
		...mapGetters(['getDaysForSearchUpdates', 'isCurrentUserExternalUser']),
		...mapState(['currentUser']),

		contextualMenuIcons() {
			let arr = [];

			if (this.item.fileExists) {
				arr.push([{ name: 'copyToClipboard', classes: 'far fa-clipboard ml-2 text-primary clickable clickable--no-hover', clickFn: this.copyLinkToClipboard, show: true, title: this.$t('copy_to_clipboard') }]);
				arr.push([{ name: 'history', classes: 'fa fa-history ml-2 text-primary clickable clickable--no-hover', clickFn: this.showHistoryVersionClickedHandler, show: true, title: this.$t('click_to_see_version_document_item') }]);
				if (!this.isCurrentUserExternalUser) {
					arr.push([{ name: 'comment', classes: 'fas fa-comment-dots ml-2 text-primary clickable clickable--no-hover', clickFn: this.commentIconClickedHandler, show: true, title: this.$t('click_to_add_comment') }]);
					arr.push([{ name: 'favorite', classes: 'fas fa-star ml-2 text-primary clickable clickable--no-hover', clickFn: this.favoriteIconClickedHandler, show: true, title: this.$t('click_to_add_favorite') }]);
				}
			}
			if (this.userCanEditItems) {
				arr.push([{ name: 'editItem', classes: 'fas fa-edit ml-2 text-primary clickable clickable--no-hover', clickFn: this.editItemClickedHandler, show: true, title: this.$t('edit_item') }]);
			}

			return arr;
		},

		getDocumentName() {
			if (this.item.title.length > 15) return `${this.item.title.substring(0, 15)}...`;

			return this.item.title;
		},

		getFavoriteClass() {
			return this.item.isFavoriteInThisSeries ? 'fas fa-star text-secondary' : 'far fa-star text-secondary';
		},

		getTitle() {
			if (this.item.title.length > 15) return this.item.title;

			return '';
		},

		hasUpdates() {
			if (!this.displayFamilyUpdates) {
				return false;
			}

			return this.item.isUpdated(this.getDaysForSearchUpdates);
		},
	},

	methods: {
		...mapMutations([
			'updateUsageTrackerStorage',
		]),

		...mapActions([
			'getLocalIP',
		]),

		bounceEffect(target) {
			target.classList.add('bounce');

			setTimeout(() => {
				target.classList.remove('bounce');
				void target.offsetWidth;
			}, 1200);
		},

		escKeyPressedHandler() {
			this.showContextMenu = false;
		},

		clickCategoryFilterHandler() {
			this.$emit('clickCategoryFilter');
		},

		clickSubcategoryFilterHandler() {
			this.$emit('clickSubcategoryFilter');
		},

		commentIconClickedHandler() {
			this.$emit('commentIconClicked');
			this.escKeyPressedHandler();
		},

		contextMenuIconClickedHandler() {
			this.showContextMenu = !this.showContextMenu;

			this.isFirstTime = true;
		},

		copyLinkToClipboard(event) {
			this.$clipboard(this.item.documentUrl);
			this.$emit('copyLinkToClipboard');
			this.bounceEffect(event.currentTarget);
		},

		editItemClickedHandler() {
			this.$emit('editItemClicked');
			this.escKeyPressedHandler();
		},

		favoriteIconClickedHandler(event) {
			this.$emit('favoriteIconClicked');

			this.bounceEffect(event.currentTarget);
		},

		itemTitleClickedHandler() {
			if (this.$AuthService.isExternalUser()) {
				utilities.downloadSharepointFile(this.item.documentUrl, this.item.title);
			} else {
				window.open(this.item.documentUrl, '_blank').focus();
			}
			this.$emit('itemTitleClicked');
		},

		mouseEnterHandler() {
			if (this.$AuthService.isExternalUser()) return;
			this.showTooltip = false;
			this.showTooltipTimer = setTimeout(() => {
				this.showTooltip = true;
			}, 750);
		},

		mouseLeaveHandler() {
			clearTimeout(this.showTooltipTimer);
		},

		onCreateTippyHandler(instance) {
			instance.show();
		},

		showHistoryVersionClickedHandler() {
			this.$emit('showHistoryVersionClicked');
			this.escKeyPressedHandler();
		},
	},
};
</script>

<style lang="scss">
	.contextual-menu-container {
		&:hover {
			.contextual-menu {
				display: inline-block;
			}
		}
	}

	.contextual-menu {
		display: none;
		position: absolute;
		top: -1px;
	}

	.favorite-icon-container {
		position: absolute;
		right: 0;
	}

	$shadow-color: #666;

	.updated-ribbon {
		overflow: hidden;
		position: relative;
		z-index: 1;
		&:after {
			background-color: green;
			box-shadow: -1px 1px 3px $shadow-color;
			content: ' ';
			height: 7px;
			position: absolute;
				right: -10px;
				top: 0px;
			text-align: center;
			text-transform: uppercase;
			transform: rotate(45deg);
			width: 50px;
		}
	}

	.status-icons-container {
		min-width: 76px;
	}
</style>
